import React, { useEffect, useRef, useState } from 'react';
import { FaChevronDown, FaChevronRight } from 'react-icons/fa';
import { GiBrain } from 'react-icons/gi';
import { ThinkingSegment } from '../../hooks/useTeamChatSocket';
import {
  markdownToSafeHtml,
  markdownToSafeInlineHtml,
} from '../../lib/markdown';

/** A reloaded turn's tool call, fetched lazily (no `input`/`output` needed to render the pill) */
export interface HistoryToolPill {
  toolCallId: string;
  functionName: string;
  status: 'succeeded' | 'failed';
}

interface ChatThinkingViewProps {
  /** Ordered thought text + tool calls, interleaved in stream order */
  segments: ThinkingSegment[];
  /** Live mode: streaming panel. Otherwise a collapsed "View thoughts" link */
  live: boolean;
  /** Opens the tool-call detail modal for a tool segment's pill */
  onOpenTool: (toolCallId: string, functionName: string) => void;
  /**
   * Reloaded turns only: a reloaded turn's `segments` carry thought text but
   * no tool calls (the wire history has no per-call data). When provided,
   * the first time "View thoughts" is expanded this fetches that turn's
   * tool calls so they can still be shown, as clickable pills, appended
   * after the thought text. Never called for live turns, and never called
   * more than once.
   */
  loadHistoryToolCalls?: () => Promise<HistoryToolPill[]>;
}

/** A single clickable tool pill, shared by inline segments and lazily-loaded history pills */
const ToolPill: React.FC<{
  toolCallId: string;
  functionName: string;
  status: 'started' | 'succeeded' | 'failed';
  onOpenTool: (toolCallId: string, functionName: string) => void;
}> = ({ toolCallId, functionName, status, onOpenTool }) => (
  <button
    type="button"
    className={`chat-tool-chip chat-tool-chip-${status}`}
    title={`${functionName}: ${status}`}
    onClick={() => onOpenTool(toolCallId, functionName)}>
    {functionName}
  </button>
);

/** Renders one segment: a run of thought text, or a clickable tool pill */
const SegmentList: React.FC<{
  segments: ThinkingSegment[];
  onOpenTool: (toolCallId: string, functionName: string) => void;
}> = ({ segments, onOpenTool }) => (
  <>
    {segments.map((segment, index) =>
      segment.kind === 'thought' ? (
        <div
          className="chat-thinking-content markdown-content"
          key={`thought-${index}`}
          dangerouslySetInnerHTML={{
            __html: markdownToSafeHtml(segment.text),
          }}
        />
      ) : (
        <ToolPill
          key={`tool-${segment.toolCallId}-${index}`}
          toolCallId={segment.toolCallId}
          functionName={segment.functionName}
          status={segment.status}
          onOpenTool={onOpenTool}
        />
      ),
    )}
  </>
);

/**
 * The agent's thinking: while a turn runs, a live auto-scrolling panel of
 * thinking tokens and tool activity, rendered inline in stream order; once
 * the response arrives, collapsed behind a "View thoughts" link (rendered
 * under the user's prompt, above the agent's response). Tool pills are
 * clickable — they open a modal with that call's input/output.
 *
 * On a reloaded turn (no inline tool segments), expanding "View thoughts"
 * lazily fetches that turn's tool calls via `loadHistoryToolCalls` and
 * appends them as pills after the thought text.
 */
const ChatThinkingView: React.FC<ChatThinkingViewProps> = ({
  segments,
  live,
  onOpenTool,
  loadHistoryToolCalls,
}) => {
  const [expanded, setExpanded] = useState(false);
  const [historyPills, setHistoryPills] = useState<HistoryToolPill[] | null>(
    null,
  );
  const [historyLoading, setHistoryLoading] = useState(false);
  const fetchedHistoryRef = useRef(false);
  const cancelHistoryFetchRef = useRef<(() => void) | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (live && scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [live, segments]);

  // On unmount, disarm any in-flight lazy history fetch so its resolution
  // can't set state on an unmounted component (matches ToolCallModal).
  useEffect(
    () => () => {
      cancelHistoryFetchRef.current?.();
    },
    [],
  );

  // Lazily fetch a reloaded turn's tool calls the first time "View thoughts"
  // is expanded. Done in an effect (not the toggle's click handler) so the
  // button stays a plain local toggle with no request work in its handler.
  // The ref guard keeps it to a single fetch regardless of re-renders.
  useEffect(() => {
    if (!expanded || !loadHistoryToolCalls || fetchedHistoryRef.current) {
      return;
    }
    fetchedHistoryRef.current = true;
    setHistoryLoading(true);
    let cancelled = false;
    cancelHistoryFetchRef.current = () => {
      cancelled = true;
    };
    loadHistoryToolCalls()
      .then((pills) => {
        if (!cancelled) setHistoryPills(pills);
      })
      .catch(() => {
        if (!cancelled) setHistoryPills([]);
      })
      .finally(() => {
        if (!cancelled) setHistoryLoading(false);
      });
  }, [expanded, loadHistoryToolCalls]);

  // Nothing to show and nothing to lazily fetch: render nothing. A reloaded
  // turn with empty thoughts but recorded tool calls has no segments yet
  // still supplies `loadHistoryToolCalls` — keep the toggle so that fetch
  // stays reachable. (A live turn never gets a loader, so it still bails.)
  if (segments.length === 0 && !loadHistoryToolCalls) {
    return null;
  }

  // The toggle is a plain local disclosure control; the lazy history fetch
  // runs from the effect above when `expanded` flips true (never for live
  // turns, which have no loader).
  const handleToggle = () => setExpanded((value) => !value);

  if (live) {
    // A live turn collapses to a single "<status>…" line — the
    // most recent tool call's model-provided action_summary, falling back
    // to the tool name, or "thinking" before any call. The full stream
    // stays available behind the same "View thoughts" toggle, and because
    // `expanded` also drives the completed-turn panel below, a panel the
    // user opened stays open when the response lands.
    const latestTool = [...segments]
      .reverse()
      .find((segment) => segment.kind === 'tool') as
      | Extract<ThinkingSegment, { kind: 'tool' }>
      | undefined;
    const statusText = latestTool
      ? latestTool.summary || `Running ${latestTool.functionName}`
      : 'Thinking';
    return (
      <div className="chat-thinking chat-thinking-live">
        <button className="chat-thinking-toggle" onClick={handleToggle}>
          {expanded ? (
            <FaChevronDown size={10} />
          ) : (
            <FaChevronRight size={10} />
          )}{' '}
          View thoughts
        </button>
        {expanded && (
          <div className="chat-thinking-segments" ref={scrollRef}>
            <SegmentList segments={segments} onOpenTool={onOpenTool} />
          </div>
        )}
        <div
          className="chat-thinking-status"
          data-testid="chat-thinking-status">
          <GiBrain className="chat-thinking-icon" />
          <span className="chat-thinking-status-text">
            <span
              dangerouslySetInnerHTML={{
                __html: markdownToSafeInlineHtml(statusText),
              }}
            />
            …
          </span>
        </div>
      </div>
    );
  }

  // When there are no thought segments the toggle reveals only tool calls,
  // so label it accordingly instead of the misleading "View thoughts".
  const hasThoughtSegments = segments.some(
    (segment) => segment.kind === 'thought',
  );
  const toggleLabel = hasThoughtSegments ? 'View thoughts' : 'View details';

  return (
    <div className="chat-thinking">
      <button className="chat-thinking-toggle" onClick={handleToggle}>
        {expanded ? <FaChevronDown size={10} /> : <FaChevronRight size={10} />}{' '}
        {toggleLabel}
      </button>
      {expanded && (
        <div className="chat-thinking-expanded">
          <SegmentList segments={segments} onOpenTool={onOpenTool} />
          {historyLoading && (
            <div
              className="chat-thinking-history-loading"
              data-testid="chat-thinking-history-loading">
              Loading tool calls…
            </div>
          )}
          {historyPills &&
            historyPills.map((pill) => (
              <ToolPill
                key={`history-tool-${pill.toolCallId}`}
                toolCallId={pill.toolCallId}
                functionName={pill.functionName}
                status={pill.status}
                onOpenTool={onOpenTool}
              />
            ))}
        </div>
      )}
    </div>
  );
};

export default ChatThinkingView;
